import { useState } from 'react'
import { FlexBox } from 'shared/components/Base/Base'
import { InlineSpinner } from 'shared/components/Spinner'
import { useUserV3 } from 'shared/hooks/query/useUserV3'
import { ProductSubscriptionBundleName } from 'shared/models/productSubscription'
import { ProfilePage, ProfilePageTab } from 'shared/pages/ProfilePage/ProfilePage'

import { useHasEnhancedSafetyTool, useHasJobListRedesign } from '../../hooks/useHasFeatureFlag'
import { useTruckV1 } from '../../hooks/useTruckV1'
import { ConsoleUserProvider } from '../Context/ConsoleUserProvider'
import { DocumentProvider } from '../Documents/DocumentList'
import { TeamLink } from '../Teams/TeamLink'
import { UserAppOnboarding } from './UserAppOnboarding'
import { UserDriverTransitionDropdown } from './UserDriverTransitionDropdown'
import { UserEmployment } from './UserEmployment'
import { UserOnboarding } from './UserOnboarding'
import { UserProgramEnrollment } from './UserProgramEnrollment'
import { UserRoleChange } from './UserRoleChange'
import { UserTeamMove } from './UserTeamMove'

interface CtOnCtProfilePageProps {
  userId: string
  permissions: any
  isInsideSidebar?: boolean
  initialTab?: ProfilePageTab
}

export enum AppOnboardingFlow {
  DRIVER_APPLICATION = 'DRIVER_APPLICATION',
  EMPLOYMENT_HISTORY = 'EMPLOYMENT_HISTORY',
}

export function CtOnCtProfilePage({
  userId,
  permissions,
  isInsideSidebar,
  initialTab,
}: CtOnCtProfilePageProps) {
  const { user, team, isLoading, refetch } = useUserV3(userId)
  const { truck: truckV1 } = useTruckV1(user?.defaultTeamMembership?.driverAssignedTruck?.id || '')
  const [appOnboardingFlow, setAppOnboardingFlow] = useState<AppOnboardingFlow | null>(null)
  const hasEnhancedSafetyToolFlag = useHasEnhancedSafetyTool()
  const hasJobListRedesignFlag = useHasJobListRedesign()

  if (isLoading || !user) return <InlineSpinner mt={5} mb={5} />

  if (appOnboardingFlow) {
    return (
      <UserAppOnboarding
        user={user}
        onClose={() => setAppOnboardingFlow(null)}
        appOnboardingFlow={appOnboardingFlow}
      />
    )
  }

  const isPremium = team?.productSubscriptionName === ProductSubscriptionBundleName.PREMIUM
  const isFlexStarter = Boolean(team?.isFlexStarter)
  const hasStarterFeature = Boolean(team?.hasFactoringFeature)
  const hasFactoringFeature = Boolean(team?.hasFactoringFeature)

  return (
    <ConsoleUserProvider
      isPremium={isPremium}
      isFlexStarter={isFlexStarter}
      hasStarterFeature={hasStarterFeature}
      hasFactoringFeature={hasFactoringFeature}
      permissions={permissions}
    >
      <FlexBox mt={2} mb={4}>
        <DocumentProvider
          permissions={permissions}
          userId={userId}
          isTeamPage={false}
          isEquipmentPage={false}
        >
          {(onDocumentOpen, onDecryptDocument, renderDocumentAction) => (
            <UserRoleChange record={user} onSuccess={() => refetch()}>
              {(handleUserRoleChange) => (
                <UserTeamMove record={user} team={team} permissions={permissions}>
                  {(handleChangeTeamClick) => (
                    <UserProgramEnrollment record={user} permissions={permissions}>
                      {(handleProgramEnrollment) => (
                        <ProfilePage
                          tabs={[
                            ...(hasEnhancedSafetyToolFlag ? [ProfilePageTab.OVERVIEW] : []),
                            ...(isInsideSidebar ? [] : [ProfilePageTab.PROFILE]),
                            ProfilePageTab.JOBS,
                            ...(isInsideSidebar ? [] : [ProfilePageTab.SETTINGS]),
                            ...(isInsideSidebar ? [] : [ProfilePageTab.DOCUMENTS]),
                            ...(isInsideSidebar ? [] : [ProfilePageTab.ISSUES]),
                          ]}
                          teamOpener={(sidebarOpener) => (
                            <TeamLink
                              team={{
                                id: team?.id ?? '',
                                name: team?.name,
                                productSubscriptionName: team?.productSubscriptionName,
                              }}
                            >
                              {sidebarOpener}
                            </TeamLink>
                          )}
                          initialTab={
                            initialTab ?? (isInsideSidebar ? ProfilePageTab.JOBS : ProfilePageTab.PROFILE)
                          }
                          user={user}
                          team={team || null}
                          assignedTruck={truckV1}
                          renderDeactivateUserButton={() => null}
                          renderOnboarding={(setActiveTab) => (
                            <UserOnboarding
                              user={user}
                              team={team}
                              permissions={permissions}
                              setActiveTab={setActiveTab}
                              setAppOnboardingFlow={setAppOnboardingFlow}
                            />
                          )}
                          renderEmployment={() => (
                            <UserEmployment user={user} refetchUser={refetch} permissions={permissions} />
                          )}
                          renderDriverTransition={() => (
                            <UserDriverTransitionDropdown
                              userId={userId}
                              permissions={permissions}
                              onSuccess={() => refetch()}
                            />
                          )}
                          onUserRoleChange={handleUserRoleChange}
                          onProgramEnrollment={handleProgramEnrollment}
                          onJobOpen={
                            (job) =>
                              window.open(
                                `/jobs/${job.id}/show`,
                                '_self'
                              ) /* history.push doesn't scroll to top*/
                          }
                          jobUrlConstructor={(job) => `/jobs/${job.id}/show`}
                          onTeamChange={handleChangeTeamClick}
                          onDocumentOpen={onDocumentOpen}
                          renderDocumentAction={renderDocumentAction}
                          isInsideSidebar={isInsideSidebar}
                          hasJobListRedesignFlag={hasJobListRedesignFlag}
                        />
                      )}
                    </UserProgramEnrollment>
                  )}
                </UserTeamMove>
              )}
            </UserRoleChange>
          )}
        </DocumentProvider>
      </FlexBox>
    </ConsoleUserProvider>
  )
}
